import { useCallback, useState } from 'react';
import { FilterRow, FilterState } from '../components/SconApp/filterBuilder/filterTypes';
import { IClientFilterParams } from '../services/api/clientService';

let nextId = 1;
const generateId = () => `filter-${nextId++}`;

const defaultField = 'lastVisit';
const defaultOperator = 'gt';

export interface UseFilterBuilderReturn {
  state: FilterState;
  hasActiveFilters: boolean;
  activeFilterCount: number;
  addRow: () => void;
  setRows: (rows: FilterRow[], operator: 'AND' | 'OR') => void;
  removeRow: (id: string) => void;
  updateRow: (id: string, field: string, operator: string, value: string | number | boolean) => void;
  clearAll: () => void;
  toFilterParams: () => IClientFilterParams;
  toQueryKey: () => Record<string, string | number | boolean | undefined>;
}

export const useFilterBuilder = (): UseFilterBuilderReturn => {
  const [state, setState] = useState<FilterState>({ rows: [], operator: 'AND' });

  const hasActiveFilters = state.rows.length > 0;
  const activeFilterCount = state.rows.length;

  const addRow = useCallback(() => {
    setState(prev => ({
      ...prev,
      rows: [...prev.rows, { id: generateId(), field: defaultField, operator: defaultOperator, value: '' }],
    }));
  }, []);

  const setRows = useCallback((rows: FilterRow[], operator: 'AND' | 'OR') => {
    setState({ rows: rows.map(r => ({ ...r, id: r.id || `filter-${Date.now()}-${Math.random().toString(36).slice(2, 7)}` })), operator });
  }, []);

  const removeRow = useCallback((id: string) => {
    setState(prev => ({
      ...prev,
      rows: prev.rows.filter(r => r.id !== id),
    }));
  }, []);

  const updateRow = useCallback((id: string, field: string, operator: string, value: string | number | boolean) => {
    setState(prev => ({
      ...prev,
      rows: prev.rows.map(r => (r.id === id ? { ...r, field, operator, value } : r)),
    }));
  }, []);

  const clearAll = useCallback(() => {
    setState({ rows: [], operator: 'AND' });
  }, []);

  const toFilterParams = useCallback((): IClientFilterParams => {
    const params: IClientFilterParams = {};
    for (const row of state.rows) {
      if (row.value === '' || row.value === undefined) continue;

      if (row.field === 'lastVisit') {
        if (row.operator === 'gt') params.lastVisitBeforeDays = Number(row.value);
        if (row.operator === 'lt') params.lastVisitAfterDays = Number(row.value);
      } else if (row.field === 'totalAppointments') {
        if (row.operator === 'gte') params.minCompletedAppointments = Number(row.value);
        if (row.operator === 'lte') params.maxCompletedAppointments = Number(row.value);
      } else if (row.field === 'whatsAppConfirmed') {
        params.whatsAppConfirmed = row.value === true || row.value === 'true';
      }
    }
    return params;
  }, [state.rows]);

  const toQueryKey = useCallback(() => {
    return toFilterParams() as Record<string, string | number | boolean | undefined>;
  }, [toFilterParams]);

  return {
    state,
    hasActiveFilters,
    activeFilterCount,
    addRow,
    setRows,
    removeRow,
    updateRow,
    clearAll,
    toFilterParams,
    toQueryKey,
  };
};
